// The Sounds page's lightbox (Daniil's request, spec docs/specs/web/SA1-sounds-page.md decision
// 8): My Generations' finished sounds in the shared full-screen preview, opened from a card's
// ⤢ "Open preview" (a click on a card still plays it in the mini player, as live's opens its
// preview player). Live's header (who made it and when), the card's own Save (live's "Add name")
// and Download with the row's "Delete generation" on the bar, the sound in live's player under
// its bars, the list's next page read near the end, and ⓘ with how it was made
// (`GenerationFacts`). A sound still being made, or failed, is not in it.
import { ActionIcon, Tooltip } from "@mantine/core";
import { Trash2 } from "lucide-react";

import { useListMembers } from "../../api/generated/endpoints/teams/teams";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { useRowActions } from "../../platform/jobs/GenerationActions";
import { outputKey } from "../../platform/jobs/output-picks";
import { GenerationFacts } from "../../platform/media/GenerationFacts";
import { MediaPreview, type PreviewItem } from "../../platform/media/MediaPreview";
import { memberName } from "../../platform/members";
import { formatDay } from "../../ui/format";
import { SoundActions } from "./SoundGeneration";
import { generationFileName, soundName, soundReady } from "./sound-jobs";
import classes from "./sounds.module.css";

/** A finished sound the lightbox shows, with its job. */
export interface PreviewSound {
  item: PreviewItem;
  job: Job;
  output: GenerationOutput;
}

/** The bar's name: the sound's (its text's start), numbered when its job made several. */
function nameOf(job: Job, output: GenerationOutput): string {
  const name = soundName(job);
  const short = name.length > 60 ? `${name.slice(0, 59)}…` : name;
  return job.outputs.length > 1 ? `${short} (${output.index + 1})` : short;
}

/** The rows' finished sounds in the list's order, each job's by their index. */
export function previewSounds(jobs: readonly Job[]): PreviewSound[] {
  return jobs.flatMap((job) =>
    job.outputs.flatMap((output) => {
      if (!soundReady(job, output) || output.url === null) return [];
      const item: PreviewItem = {
        id: outputKey({ jobId: job.id, index: output.index }),
        name: nameOf(job, output),
        kind: "audio",
        url: output.url,
        thumb: null,
        poster: null,
        durationMs: output.duration_ms,
        width: null,
        height: null,
      };
      return [{ item, job, output }];
    }),
  );
}

export interface SoundPreviewProps {
  teamId: string;
  brandId: string;
  sounds: readonly PreviewSound[];
  current: string | null;
  onShow: (id: string | null) => void;
  more: { has: boolean; loading: boolean; load: () => void };
  /** Reads the list again, for a fresh link once one expired. */
  readAgain: () => void;
}

export function SoundPreview(props: SoundPreviewProps) {
  const { teamId, brandId, sounds } = props;
  // Read only while a sound is shown.
  const members = useListMembers(teamId, { query: { enabled: props.current !== null } }).data ?? [];
  const find = (item: PreviewItem) => sounds.find((each) => each.item.id === item.id);
  const maker = (job: Job) =>
    job.created_by === null ? "Unknown" : memberName(members.find((member) => member.user_id === job.created_by));
  return (
    <MediaPreview
      items={sounds.map((each) => each.item)}
      current={props.current}
      onShow={props.onShow}
      more={props.more}
      byline={(item) => {
        const shown = find(item);
        return shown === undefined ? null : `${maker(shown.job)} · ${formatDay(shown.job.created_at)}`;
      }}
      actions={(item) => {
        const shown = find(item);
        return shown === undefined ? null : (
          <SoundBar key={item.id} {...shown} teamId={teamId} brandId={brandId} readAgain={props.readAgain} />
        );
      }}
      details={(item) => {
        const shown = find(item);
        return shown === undefined ? null : (
          <GenerationFacts teamId={teamId} brandId={brandId} job={shown.job} output={shown.output} />
        );
      }}
    />
  );
}

/**
 * The bar's actions on the sound shown: its card's Save (live's "Add name") and Download, then
 * the row's "Delete generation", which deletes the job, as the row's does.
 */
function SoundBar(props: PreviewSound & { teamId: string; brandId: string; readAgain: () => void }) {
  const { teamId, brandId, job, output, readAgain } = props;
  const actions = useRowActions({
    teamId,
    brandId,
    jobs: [job],
    readAgain,
    toProject: false,
    fileName: generationFileName,
    askName: true,
  });
  const saved = actions.saved.has(outputKey({ jobId: job.id, index: output.index })) || (output.saved_asset_id ?? null) !== null;
  return (
    <div className={classes.previewBar}>
      <SoundActions
        job={job}
        output={output}
        size={18}
        saved={saved}
        saving={actions.saving}
        onSave={(name, done) => actions.saveOne({ jobId: job.id, index: output.index }, { name, onSaved: done })}
        readAgain={readAgain}
      />
      <Tooltip label="Delete generation">
        <ActionIcon
          variant="transparent"
          c="inherit"
          aria-label="Delete generation"
          disabled={!actions.deletable}
          onClick={actions.deleteGeneration}
        >
          <Trash2 size={18} strokeWidth={1.5} aria-hidden />
        </ActionIcon>
      </Tooltip>
      {actions.notes === null ? null : <div className={classes.previewNotes}>{actions.notes}</div>}
    </div>
  );
}
